import apiClient from './api';

/**
 * Blog Post API Service
 *
 * Typed client for `/api/blog/*` — pulling a Google Doc into a draft blog
 * post, listing/previewing, publishing (publish/draft/schedule to
 * WordPress), and soft-deleting. See SPEC.md Phase 1 and
 * `backend/src/services/blog-post.service.ts` for the authoritative DTOs.
 */

/**
 * Long-running blog operations (Google Doc pull + image downloads, WP media
 * uploads + post creation, AI social generation) can comfortably exceed the
 * shared axios client's 10s default (see `api.ts`) — a publish with a
 * handful of images has been observed taking ~27s, and more images or a Doc
 * re-pull can exceed 60s. Use this instead of the default on those calls so
 * a slow-but-successful backend operation doesn't surface as a misleading
 * "Unable to connect to server" network error.
 */
export const BLOG_LONG_TIMEOUT = 120_000;

export type BlogPostStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHED' | 'FAILED' | 'TRASHED';

export interface BlogImagePreview {
  imageIndex: number;
  mediaId: number;
  url: string;
  isFeatured: boolean;
}

/** Response of POST /api/blog/pull */
export interface PullFromDocResult {
  blogPostId: number;
  title: string;
  previewHtml: string;
  gdocId: string;
  gdocUrl: string;
  featuredMediaId: number | null;
  images: BlogImagePreview[];
  /** True when this Doc already had a non-deleted BlogPost for this team —
   *  the EXISTING blog (identified by `blogPostId` above) was returned
   *  instead of creating a duplicate. Phase 3 re-pull dedupe guard. */
  isExisting: boolean;
}

/** Author of a blog post — `null` when the creating user has since been
 *  removed from the team. */
export interface BlogPostAuthor {
  id: number;
  fullName: string;
}

export interface PullFromDocInput {
  /** Google Doc/file ID returned by the Google Picker. */
  docInput: string;
  /** Explicit GDrive channel to read with — omit to use the team's active one. */
  gdriveChannelId?: number;
}

/** BlogPost row (list item) — matches the Prisma `BlogPost` model shape 1:1. */
export interface BlogPost {
  id: number;
  teamId: number;
  userId: number;
  channelId: number | null;
  title: string;
  slug: string | null;
  excerpt: string | null;
  htmlBody: string;
  status: BlogPostStatus;
  featuredMediaId: number | null;
  wpFeaturedMediaId: number | null;
  wpPostId: number | null;
  wpPostUrl: string | null;
  categoryIds: number[];
  tagIds: number[];
  gdocId: string | null;
  gdocUrl: string | null;
  lastError: string | null;
  retryCount: number;
  scheduledAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Raw WordPress status the backend last saw on its live reconciliation
   *  pass (e.g. 'publish' | 'future' | 'draft' | 'trash' | 'deleted') —
   *  `null` before the first live check ever ran. */
  wpStatus?: string | null;
  /** When the backend last reconciled `status`/`wpStatus` against the live
   *  WordPress post — `null` before the first check. */
  wpStatusCheckedAt?: string | null;
}

export interface BlogPostImage {
  id: number;
  blogPostId: number;
  mediaId: number;
  order: number;
  isFeatured: boolean;
  wpMediaId: number | null;
  wpSourceUrl: string | null;
  createdAt: string;
  media: {
    id: number;
    url: string;
    type: string;
    metadata: Record<string, unknown> | null;
  };
}

/** GET /api/blog/:id response — full BlogPost with relations for the preview/confirm screen. */
export interface BlogPostWithRelations extends BlogPost {
  images: BlogPostImage[];
  channel: { id: number; platform: string; platformUsername: string | null; isActive: boolean } | null;
  featuredMedia: { id: number; url: string } | null;
  /** Who created this blog post — `null` if the user was removed. */
  author: BlogPostAuthor | null;
  /** Total pulled images (mirrors `images.length` but avoids re-deriving it). */
  imageCount: number;
  /** Number of draft social `Post` rows generated from this blog
   *  (`POST /:id/generate-social`), across all generation batches. */
  socialDraftsCount: number;
  /** Ids of the generated draft posts, most-recent first, capped at 50 —
   *  lets the UI deep-link into Content/Calendar without a second round-trip. */
  socialDraftPostIds: number[];
}

/** GET /api/blog (list) row — `BlogPost` plus just the featured image's
 *  `{ id, url }` (not the full `images` relation) so the list UI can render
 *  a real thumbnail instead of the placeholder. */
export interface BlogPostListItem extends BlogPost {
  featuredMedia: { id: number; url: string } | null;
  /** Who created this blog post — `null` if the user was removed. */
  author: BlogPostAuthor | null;
  /** Total pulled images for this blog. */
  imageCount: number;
  /** Number of draft social `Post` rows generated from this blog. */
  socialDraftsCount: number;
}

export interface ListBlogPostsParams {
  status?: BlogPostStatus;
  page?: number;
  limit?: number;
}

export interface PaginatedBlogPosts {
  items: BlogPostListItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export type PublishBlogAction = 'publish' | 'draft' | 'schedule';

export interface PublishBlogInput {
  action: PublishBlogAction;
  channelId?: number;
  title?: string;
  excerpt?: string;
  slug?: string;
  featuredMediaId?: number;
  categoryIds?: number[];
  tagIds?: number[];
  /**
   * Naive local wall-clock datetime string (NO timezone offset), e.g.
   * "2026-07-25T14:30:00". Required when action === 'schedule'. The backend
   * resolves this through the timezone cascade (user -> team -> Europe/Berlin).
   */
  scheduledAt?: string;
}

// ─── Phase 3: update existing WP post (edit-in-place) ──────────────────────

export interface UpdateBlogOnWpInput {
  /** Re-pulls the source Google Doc and rebuilds `htmlBody` from it — any
   *  newly discovered Doc images are uploaded (existing ones are untouched).
   *  When false/omitted, the currently stored `htmlBody` is pushed as-is —
   *  only title/excerpt/slug/categories/tags/featured-image may change. */
  refreshFromDoc?: boolean;
  title?: string;
  excerpt?: string;
  slug?: string;
  featuredMediaId?: number;
  categoryIds?: number[];
  tagIds?: number[];
}

/** Response of POST /api/blog/:id/update-wp — the full updated blog (same
 *  shape as `GET /api/blog/:id`) plus `refreshedFromDoc`, so the caller can
 *  show "refreshed from Google Doc" feedback without a second round-trip. */
export type UpdateBlogOnWpResult = BlogPostWithRelations & { refreshedFromDoc: boolean };

export interface PublishBlogResult {
  blogPostId: number;
  wpPostId: number;
  wpPostUrl: string;
  status: BlogPostStatus;
  /** True when the call was a no-op because the blog was already published
   *  (dupe-guard short-circuit) — no new WordPress call was made. */
  alreadyPublished: boolean;
  /** True when the publish succeeded but the backend couldn't re-pull the
   *  latest Google Doc content, so it published the last-synced (stored)
   *  content instead. Non-blocking — surface as a warning, not an error. */
  docRefreshFailed?: boolean;
}

// ─── Phase 2: Blog → social repurposing (SPEC.md decision C) ──────────────

export type SocialGenerationTone = 'professional' | 'casual' | 'edgy' | 'inspirational';

export interface GenerateSocialFromBlogInput {
  tone: SocialGenerationTone;
  /** Free-text steering, e.g. "focus on the career-change angle". */
  customInstructions?: string;
  /** Team's connected SOCIAL channel ids to draft posts on. */
  channelIds: number[];
  /** How many post drafts to generate — backend default 5, range ~3-10. */
  count?: number;
}

export interface GeneratedSocialPost {
  postId: number;
  platform: string;
  caption: string;
  channelIds: number[];
}

/** Response of POST /api/blog/:id/generate-social */
export interface GenerateSocialFromBlogResult {
  createdPosts: GeneratedSocialPost[];
  tokensUsed: number;
}

/** Team setting — GET/PUT /api/blog/settings/auto-generate */
export interface BlogAutoGenerateSetting {
  autoGenerateSocialOnBlogPublish: boolean;
}

// ─── Featured image: team media library + direct upload ────────────────────

/**
 * A team `media`-table row (Canva imports + blog images pulled across the
 * team's posts) — the source for `GET /api/blog/media-images`. Distinct from
 * `MediaLibraryItem` (the `post_media` table backing `MediaPicker`/
 * `useMediaLibraryInfinite`) — do NOT mix the two.
 */
export interface TeamMediaImage {
  id: number;
  url: string;
  type: string;
  labels: string[];
  createdAt: string;
}

export interface ListTeamMediaImagesParams {
  page?: number;
  limit?: number;
}

export interface PaginatedTeamMediaImages {
  items: TeamMediaImage[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/** Response of POST /api/blog/featured-image/upload */
export interface UploadedFeaturedImage {
  id: number;
  url: string;
  type: string;
}

export const blogApi = {
  /**
   * POST /api/blog/pull
   * Requires the team to have a connected Google Drive channel — the
   * backend throws a 400 ValidationError otherwise ("Connect a Google Drive
   * account before pulling a blog post from a Google Doc.").
   */
  pull: async (input: PullFromDocInput): Promise<PullFromDocResult> => {
    const response = await apiClient.post<{ success: true; data: PullFromDocResult }>(
      '/api/blog/pull',
      input,
      { timeout: BLOG_LONG_TIMEOUT }
    );
    return response.data.data;
  },

  /**
   * GET /api/blog
   */
  list: async (params?: ListBlogPostsParams): Promise<PaginatedBlogPosts> => {
    const response = await apiClient.get<{ success: true; data: PaginatedBlogPosts }>('/api/blog', {
      params,
    });
    return response.data.data;
  },

  /**
   * GET /api/blog/:id
   */
  getById: async (blogPostId: number): Promise<BlogPostWithRelations> => {
    const response = await apiClient.get<{ success: true; data: BlogPostWithRelations }>(
      `/api/blog/${blogPostId}`
    );
    return response.data.data;
  },

  /**
   * POST /api/blog/:id/publish
   * Dupe-guarded server-side — safe to retry after a FAILED status.
   */
  publish: async (blogPostId: number, input: PublishBlogInput): Promise<PublishBlogResult> => {
    const response = await apiClient.post<{ success: true; data: PublishBlogResult }>(
      `/api/blog/${blogPostId}/publish`,
      input,
      { timeout: BLOG_LONG_TIMEOUT }
    );
    return response.data.data;
  },

  /**
   * DELETE /api/blog/:id
   * Soft-delete only — never touches the live WordPress post.
   */
  remove: async (blogPostId: number): Promise<void> => {
    await apiClient.delete(`/api/blog/${blogPostId}`);
  },

  /**
   * POST /api/blog/:id/sync-status
   * Forces a live re-check of the blog against its WordPress post (the
   * detail screen already triggers a throttled 60s lazy re-sync on load —
   * this is for an explicit "Refresh status" action). 400 if the blog was
   * never published to WP, 404 if not found, 502 if the WP check itself
   * failed (site unreachable, auth revoked, etc).
   */
  syncStatus: async (blogPostId: number): Promise<BlogPostWithRelations> => {
    const response = await apiClient.post<{ success: true; data: BlogPostWithRelations }>(
      `/api/blog/${blogPostId}/sync-status`
    );
    return response.data.data;
  },

  /**
   * POST /api/blog/:id/update-wp
   * Phase 3 — updates the blog post's EXISTING WordPress post in place (no
   * new WP post is ever created; requires the blog to already have a
   * `wpPostId`, i.e. it has already gone through `/publish` at least once).
   */
  updateOnWp: async (blogPostId: number, input: UpdateBlogOnWpInput): Promise<UpdateBlogOnWpResult> => {
    const response = await apiClient.post<{ success: true; data: UpdateBlogOnWpResult }>(
      `/api/blog/${blogPostId}/update-wp`,
      input,
      { timeout: BLOG_LONG_TIMEOUT }
    );
    return response.data.data;
  },

  /**
   * POST /api/blog/:id/generate-social
   * AI-generates social post drafts from a published/scheduled blog post
   * (SPEC.md Phase 2, decision C). Drafts only — never auto-scheduled.
   * Can take ~10-20s (AI generation) — callers must show a dedicated
   * loading state. Errors: 402/403 when out of AI tokens or feature-gated.
   */
  generateSocial: async (
    blogPostId: number,
    input: GenerateSocialFromBlogInput
  ): Promise<GenerateSocialFromBlogResult> => {
    const response = await apiClient.post<{ success: true; data: GenerateSocialFromBlogResult }>(
      `/api/blog/${blogPostId}/generate-social`,
      input,
      { timeout: BLOG_LONG_TIMEOUT }
    );
    return response.data.data;
  },

  /**
   * GET /api/blog/settings/auto-generate
   * Team-level "auto-generate social drafts when I publish a blog" setting.
   */
  getAutoGenerateSetting: async (): Promise<BlogAutoGenerateSetting> => {
    const response = await apiClient.get<{ success: true; data: BlogAutoGenerateSetting }>(
      '/api/blog/settings/auto-generate'
    );
    return response.data.data;
  },

  /**
   * PUT /api/blog/settings/auto-generate
   */
  updateAutoGenerateSetting: async (
    autoGenerateSocialOnBlogPublish: boolean
  ): Promise<BlogAutoGenerateSetting> => {
    const response = await apiClient.put<{ success: true; data: BlogAutoGenerateSetting }>(
      '/api/blog/settings/auto-generate',
      { autoGenerateSocialOnBlogPublish }
    );
    return response.data.data;
  },

  /**
   * GET /api/blog/media-images
   * Team's `media`-table images (Canva imports + blog images across the
   * team's posts) — for the featured-image "From library" picker. Unwraps
   * the `{ data, pagination }` envelope into a single object, consistent
   * with the other paginated methods on this service.
   */
  getMediaImages: async (params?: ListTeamMediaImagesParams): Promise<PaginatedTeamMediaImages> => {
    const response = await apiClient.get<{
      success: true;
      data: TeamMediaImage[];
      pagination: PaginatedTeamMediaImages['pagination'];
    }>('/api/blog/media-images', { params });
    return { items: response.data.data, pagination: response.data.pagination };
  },

  /**
   * POST /api/blog/featured-image/upload
   * Multipart upload of a new featured image (png/jpeg/gif/webp, <=10MB).
   */
  uploadFeaturedImage: async (file: File): Promise<UploadedFeaturedImage> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ success: true; data: UploadedFeaturedImage }>(
      '/api/blog/featured-image/upload',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: BLOG_LONG_TIMEOUT,
      }
    );
    return response.data.data;
  },
};

export default blogApi;
